'use client';

import { AppSwitcher } from '@/features/app-config';
import { LanguageSwitcher } from '@/features/i18n';
import { ThemeSwitch } from '@/features/theme';
import { Link } from '@/ui/Link';
import { useLocale } from '@vimond/i18n/hooks/useLocale';
import {
  MenuItemType,
  MenuType,
  PageType,
} from '@vimond/services/content-discovery';
import { getEnvVar } from '@vimond/utils/env';
import Image from 'next/image';
import { Suspense, type JSX } from 'react';

type Props = {
  menu: MenuType | undefined;
  pages: readonly PageType[];
};

export const Footer = ({ menu: globalMenu, pages }: Props) => {
  const { pathnameWithoutLocale: pathname } = useLocale();

  const getLogoAndImages = () => {
    let leftLogo: JSX.Element | null = null;
    let rightImages: JSX.Element[] = [];

    if (globalMenu) {
      const logoItem = globalMenu.elements?.find(
        (el) => el.contentType === 'menu_item' && el.title === 'Logo',
      ) as MenuItemType | undefined;

      if (logoItem?.images?.defaultUrl) {
        const logoImg = (
          <Image
            src={logoItem.images.defaultUrl}
            alt="Footer Logo"
            width={0}
            height={0}
            sizes="100vw"
            className="h-auto max-h-10 w-full max-w-full object-contain"
          />
        );
        leftLogo = logoItem.url ? (
          <Link href={logoItem.url}>{logoImg}</Link>
        ) : (
          logoImg
        );
      }

      const imageElements = (globalMenu?.elements ?? []).filter(
        (el) =>
          el.contentType === 'menu_item' &&
          el.title !== 'Logo' &&
          el.images?.defaultUrl,
      ) as MenuItemType[];

      rightImages = imageElements?.map((item) => {
        const image = (
          <Image
            src={item.images!.defaultUrl}
            alt={item.title ?? 'Image'}
            width={56} // 3.5rem = 56px
            height={56}
            className="h-[3.5rem] w-[3.5rem] object-contain"
          />
        );
        return item.url ? <Link href={item.url}>{image}</Link> : image;
      });
    }

    return { leftLogo, rightImages };
  };

  const getFooterMenu = (pages: readonly PageType[]) => {
    const page = pages.find(
      (p) =>
        p['page-slug'] === pathname ||
        (pathname === '/browse' && p['page-slug'] === '/'),
    );

    const footerMenu = page?.elements?.find(
      (el) =>
        el.contentType === 'menu' && el['layout-menu-position'] === 'Footer',
    ) as MenuType;

    return (
      ((footerMenu?.elements ?? globalMenu?.elements) as MenuItemType[]) || []
    );
  };

  const menu = getFooterMenu(pages);
  if (menu?.length === 0) return null;

  const { leftLogo, rightImages } = getLogoAndImages();

  return (
    <footer className="m-3 text-xs">
      <div className="mx-auto w-full max-w-screen-xl flex flex-col gap-4 p-4">
        {/* Logo & Right Images Row */}
        <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex justify-center sm:justify-start">{leftLogo}</div>
          <div className="flex justify-center sm:justify-end w-full sm:w-auto">
            <div className="flex flex-wrap justify-center sm:justify-end gap-4 max-w-full overflow-x-auto sm:overflow-visible">
              {rightImages}
            </div>
          </div>
        </div>

        {/* Footer Menu & Switchers Row */}
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:items-center sm:justify-between w-full">
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <div className="flex items-center justify-center gap-4">
              <Suspense>
                <ThemeSwitch menuposition="over" />
              </Suspense>
              <Suspense>
                <LanguageSwitcher menuposition="over" />
              </Suspense>
            </div>
          </div>

          {/* Menu list row */}
          <ul className="mt-3 flex flex-wrap items-center font-medium justify-center sm:justify-end sm:w-auto w-full">
            {menu?.map((item) =>
              !item.images?.defaultUrl ? (
                <li key={item.title}>
                  <Link
                    href={item.url}
                    className="me-4 hover:underline md:me-6"
                  >
                    {item.title}
                  </Link>
                </li>
              ) : null,
            )}
          </ul>
        </div>

        {/* Powered by Vimond row (new line) */}
        {getEnvVar('NEXT_PUBLIC_POWERED_BY_ENABLED') !== 'false' && (
          <div className="w-full text-center sm:text-left text-text-secondary">
            Powered by{' '}
            <a
              href="https://www.vimond.com"
              target="_blank"
              className="text-button-primary hover:text-text-highlight"
            >
              Vimond VIA
            </a>
          </div>
        )}
      </div>

      <AppSwitcher />
    </footer>
  );
};
